import React from "react";
import { CmpMemberStatusEnum } from "../../../../graphql/types";
import { DeleteMemberBlockedReason } from "../MemberDeleteNotices";

/**
 * IF11 MB8 Phase A/B: the Update Member status panel for Active, Cancelling,
 * Suspended, Cancelled, Deleted and Past where the status model is on
 * (Sand Bay first). CMP Dashboard (21) CD:6846-6975, DCL "Member status pill
 * reflects the latest action", "Cancel Member Renewal -> Cancelling" and
 * "Member list and report status values". Pending keeps the existing panel.
 */
export type MemberStatusModelPanelProps = {
  memberStatus: CmpMemberStatusEnum;
  /** Cancelling: the renewal (end) date; Cancelled/Deleted/Past: the cancel or delete date. */
  statusDateLabel: string;
  recallUntilLabel: string;
  startDateLabel: string;
  renewalDateLabel: string;
  renewalSentLabel?: string | null;
  autoRenewEnabled: boolean;
  /** No renewal invoice goes to a No Charge member; the date rolls silently. */
  noCharge?: boolean;
  suspendedOnLabel?: string | null;
  /** Past after a cancel at renewal (not a delete). */
  endedByCancel: boolean;
  actionLoading: boolean;
  canSuspend: boolean;
  canUnsuspend: boolean;
  canDelete: boolean;
  /** Monday 13389365001 (S9): why Delete Member is grey (backend archive readiness). */
  deleteBlockedReason?: string | null;
  onSuspend: () => void;
  onUnsuspend: () => void;
  onCancelRenewal: () => void;
  onUncancelRenewal: () => void;
  onDelete: () => void;
  /** The existing "Submit New Company Membership Application" action. */
  reapplyAction?: React.ReactNode;
  /** Rows the Active panel keeps below the dates (for example the membership invoice status). */
  activeExtra?: React.ReactNode;
};

const REPORTS_NOTE = "Members can be filtered by member status in “Reports”.";
const pillClass = "w-fit rounded-[10px] border px-[14px] py-[8px] text-[14px] font-semibold leading-[1.3] text-white";
const Pill = ({ children, red = false }: { children: React.ReactNode; red?: boolean }) => (
  <div className={`${pillClass} ${red ? "border-[#F21B1B] bg-[#F21B1B]" : "border-[#10B981] bg-[#10B981]"}`}>{children}</div>
);
const StatusIcon = ({ color }: { color: string }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="mt-[1px] h-[22px] w-[22px] shrink-0">
    <circle cx="12" cy="12" r="10" /><path d="m8.5 12.2 2.4 2.4 4.6-4.8" />
  </svg>
);
const buttonClass = "inline-flex items-center gap-[8px] whitespace-nowrap rounded-[8px] border px-[20px] py-[12px] font-['DM_Sans',sans-serif] text-[14px] font-semibold leading-[1.2] text-white disabled:cursor-not-allowed disabled:opacity-60";
const ActionColumn = ({ helper, children }: { helper: React.ReactNode; children: React.ReactNode }) => (
  <div className="grid content-start justify-items-start gap-y-[10px] md:row-span-3 md:grid-rows-[subgrid]">
    <span className="text-[13px] leading-[1.5] text-[#0A0A0A]">{helper}</span>
    {children}
  </div>
);
const CalendarIcon = ({ undo }: { undo: boolean }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[15px] w-[15px] shrink-0">
    <rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />{undo ? <path d="m9 16 2 2 4-4" /> : <path d="m14.5 14.5-5 5M9.5 14.5l5 5" />}
  </svg>
);
const TrashIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[14px] w-[14px] shrink-0">
    <path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const PersonIcon = ({ unsuspend }: { unsuspend: boolean }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[15px] w-[15px] shrink-0">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />{unsuspend ? <path d="m16 11 2 2 4-4" /> : <><path d="M17 9v6" /><path d="M21 9v6" /></>}
  </svg>
);

const MemberStatusModelPanel = (props: MemberStatusModelPanelProps) => {
  const { memberStatus, actionLoading } = props;
  const cancelling = memberStatus === CmpMemberStatusEnum.Cancelling;
  const ended = memberStatus === CmpMemberStatusEnum.Cancelled || memberStatus === CmpMemberStatusEnum.Deleted ||
    memberStatus === CmpMemberStatusEnum.Past;

  const deleteButton = (
    <>
      <button type="button" className={`${buttonClass} border-none bg-[#F21B1B]`} disabled={!props.canDelete || actionLoading} onClick={props.onDelete}>
        <TrashIcon />Delete Member
      </button>
      {!props.canDelete && <DeleteMemberBlockedReason reason={props.deleteBlockedReason} />}
    </>
  );
  const cancelButton = cancelling ? (
    <button type="button" className={`${buttonClass} border-[#E1A731] bg-[#E1A731] hover:border-[#C6912A] hover:bg-[#C6912A]`} disabled={actionLoading} onClick={props.onUncancelRenewal}>
      <CalendarIcon undo />Uncancel Member Renewal
    </button>
  ) : (
    <button type="button" className={`${buttonClass} border-[#E1A731] bg-[#E1A731] hover:border-[#C6912A] hover:bg-[#C6912A]`} disabled={actionLoading} onClick={props.onCancelRenewal}>
      <CalendarIcon undo={false} />Cancel Member Renewal
    </button>
  );
  const cancelPendingHelper = `The renewal for this membership has been cancelled and it will end on the renewal date. Click “Uncancel Member Renewal” to keep the membership. The member returns to Active status${props.noCharge ? "." : " and the renewal invoice will be sent automatically."}`;

  if (ended) {
    const deleted = !props.endedByCancel;
    const past = memberStatus === CmpMemberStatusEnum.Past;
    const undated = past && !props.statusDateLabel;
    return (
      <>
        <div className="flex items-start gap-[14px]">
          <StatusIcon color="#0A0A0A" />
          <div>
            <div className="flex flex-wrap items-center gap-[14px]">
              {past ? <Pill red>Past Member</Pill> : <Pill>{deleted ? "Deleted" : "Cancelled"}</Pill>}
              {props.statusDateLabel ? (
                <span className="text-[14px] text-[#0A0A0A]">{deleted ? "Deleted effective" : "Membership cancelled"} <span className="font-semibold">{props.statusDateLabel}</span></span>
              ) : null}
            </div>
            <div className="mt-[4px] text-[14px] leading-[1.6] text-[#0A0A0A]">
              {undated ? (
                // Q36: a Past member with no delete date (imports, legacy). No design text; the design's Past text without the 365-day clause.
                <>This member is a <b>Past Member</b>. Member privileges, directory listings and access to the Member Dashboard are <b>denied</b> for all member contacts. Member data is view-only and can no longer be recalled; a new membership is required if the member rejoins. {REPORTS_NOTE}</>
              ) : past ? (
                <>This member was {deleted ? "deleted" : "cancelled"} more than 365 days ago and is now a <b>Past Member</b>. Member privileges, directory listings and access to the Member Dashboard are <b>denied</b> for all member contacts. Member data is view-only and can no longer be recalled; a new membership is required if the member rejoins. {REPORTS_NOTE}</>
              ) : deleted ? (
                <>This member is deleted and no longer has member privileges or directory listings, and access to the Member Dashboard is <b>denied</b> for all member contacts. Member data can be recalled until <b>{props.recallUntilLabel}</b> when creating a new member; after that the member moves to <b>Past Member</b>. {REPORTS_NOTE}</>
              ) : (
                <>This membership was cancelled on the renewal date and no longer has member privileges or directory listings, and access to the Member Dashboard is <b>denied</b> for all member contacts. Member data can be recalled until <b>{props.recallUntilLabel}</b> when creating a new member; after that the member moves to <b>Past Member</b>. {REPORTS_NOTE}</>
              )}
            </div>
          </div>
        </div>
        {props.reapplyAction}
      </>
    );
  }

  if (memberStatus === CmpMemberStatusEnum.Suspended) {
    return (
      <>
        <div className="flex items-start gap-[14px]">
          <StatusIcon color="#0A0A0A" />
          <div>
            <div className="flex flex-wrap items-center gap-[14px]">
              <Pill>Suspended Member</Pill>
              {props.suspendedOnLabel ? <span className="text-[14px] text-[#0A0A0A]">Suspended effective <span className="font-semibold">{props.suspendedOnLabel}</span></span> : null}
            </div>
            <div className="mt-[10px] text-[14px] leading-[1.6] text-[#0A0A0A]">
              This member is currently suspended. All member privileges, directory listings, and member dashboard access for all contacts have been temporarily disabled. {REPORTS_NOTE}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-y-[18px] md:grid-cols-3 md:grid-rows-[auto_auto] md:gap-x-[40px] md:gap-y-[10px]">
          <ActionColumn helper="Click “Unsuspend Member” to automatically restore all privileges, directory listings, and previously authorized Member Dashboard access.">
            <button type="button" className={`${buttonClass} border-[#26282C] bg-[#26282C] hover:border-[#0A0A0A] hover:bg-[#0A0A0A]`} disabled={!props.canUnsuspend || actionLoading} onClick={props.onUnsuspend}>
              <PersonIcon unsuspend />Unsuspend Member
            </button>
          </ActionColumn>
          <ActionColumn helper="To cancel the membership at its renewal date, select “Cancel Member Renewal.” No renewal invoice will be sent. The member shows as “Cancelling” until the renewal date, then “Cancelled.” Member data can be recalled for 365 days after the renewal date when creating a new member; after that the member moves to “Past Member.”">
            {cancelButton}
          </ActionColumn>
          <ActionColumn helper="To immediately cancel the membership, click “Delete Member.” Member privileges, directory listings and Member Dashboard access are removed immediately, and the member’s status will change to “Deleted.” Member data can be recalled for 365 days when creating a new member; after that the member moves to “Past Member.”">
            {deleteButton}
          </ActionColumn>
        </div>
      </>
    );
  }

  // Active or Cancelling.
  const badge = props.autoRenewEnabled && cancelling
    ? { className: "border-[#F0202E] bg-[#FEF2F2]", stroke: "#F0202E", text: "Credit Card Auto-Renewal Cancelled", textClass: "text-[#F0202E]", cross: true }
    : props.autoRenewEnabled
      ? { className: "border-[#10B981] bg-[#F0FBF7]", stroke: "#10B981", text: "Credit Card Auto-Renewal Enabled", textClass: "text-[#0A0A0A]", cross: false }
      : { className: "border-[#9E9E9E] bg-[#F7F7F8]", stroke: "#727272", text: "Auto-Renewal Not Enabled", textClass: "text-[#0A0A0A]", cross: false };
  return (
    <>
      <div className="flex items-start gap-[14px]">
        <StatusIcon color="#10B981" />
        <div className="flex min-w-0 flex-col gap-[12px]">
          {cancelling ? (
            <div className="flex flex-wrap items-center gap-[14px]">
              <Pill>Cancelling</Pill>
              <span className="text-[14px] text-[#0A0A0A]">Membership ends <span className="font-semibold text-[#F0202E]">{props.statusDateLabel}</span></span>
            </div>
          ) : <Pill>Active Member</Pill>}
          <div className="text-[14px] leading-[1.6] text-[#0A0A0A]">
            {cancelling
              ? `The member is set to have its membership cancelled on the renewal date. All member privileges, directory listing and Member Dashboard access will be disabled. No Renewal invoice will be Sent. ${REPORTS_NOTE}`
              : props.noCharge
                ? `This member is active. All member privileges, directory listings, and Member Dashboard access for authorized contacts are enabled. This is a No Charge membership, so no renewal invoice will be sent. ${REPORTS_NOTE}`
              : props.autoRenewEnabled
                ? `This member is active. All member privileges, directory listings, and Member Dashboard access for authorized contacts are enabled. The member has chosen to auto-renew by credit card, shown as Credit Card Auto-Renewal Enabled below, so the card on file will be charged automatically on the renewal date. ${REPORTS_NOTE}`
                : `This member is active. All member privileges, directory listings, and Member Dashboard access for authorized contacts are enabled. The member has not chosen to auto-renew by credit card, shown as Auto-Renewal Not Enabled below, so the renewal invoice will be sent 30 days before the renewal date. ${REPORTS_NOTE}`}
          </div>
          <div className="flex flex-wrap items-start gap-[36px]">
            <div className="flex items-center gap-[9px]">
              <svg aria-hidden="true" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#0A0A0A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
              <span className="text-[14px]">Membership Start Date: <span className="text-[16px] font-semibold">{props.startDateLabel}</span></span>
            </div>
            <div className="flex flex-col gap-[6px]">
              <div className="flex items-center gap-[9px]">
                <svg aria-hidden="true" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#0A0A0A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><path d="M3 12a9 9 0 0 1 15.36-6.36L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15.36 6.36L3 16" /><path d="M3 21v-5h5" /></svg>
                <span className="text-[14px]">{cancelling ? "Membership Cancel Date:" : "Membership Renewal Date:"} <span className={`text-[16px] font-semibold ${cancelling ? "text-[#F0202E]" : ""}`}>{props.renewalDateLabel}</span></span>
              </div>
              {props.renewalSentLabel ? (
                <div className="flex items-center gap-[9px]">
                  <svg aria-hidden="true" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#F0202E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>
                  <span className="text-[14px] font-semibold text-[#F0202E]">{props.renewalSentLabel}</span>
                </div>
              ) : null}
            </div>
            <div className={`flex w-fit items-center gap-[10px] rounded-[10px] border px-[14px] py-[8px] ${badge.className}`}>
              <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke={badge.stroke} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v5h-5" />{badge.cross ? <path d="m9 9 6 6M15 9l-6 6" /> : null}</svg>
              <span className={`whitespace-nowrap text-[14px] font-semibold ${badge.textClass}`}>{badge.text}</span>
            </div>
          </div>
          {props.activeExtra}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-y-[18px] md:grid-cols-3 md:grid-rows-[auto_auto_auto] md:gap-x-[40px] md:gap-y-[10px]">
        <ActionColumn helper="Use to temporarily suspend membership privileges, directory listings, and member dashboard access. When the suspension is lifted, all privileges, listings and access will be automatically restored.">
          <button type="button" className={`${buttonClass} border-[#26282C] bg-[#26282C] hover:border-[#0A0A0A] hover:bg-[#0A0A0A]`} disabled={!props.canSuspend || actionLoading} onClick={props.onSuspend}>
            <PersonIcon unsuspend={false} />Suspend Member
          </button>
        </ActionColumn>
        <ActionColumn helper={cancelling ? cancelPendingHelper : `Use to keep a membership active until its renewal date, when it will automatically be cancelled. No renewal invoice will be sent. The member shows as “Cancelling” until the renewal date, then “Cancelled.” Member data can be recalled for 365 days after the renewal date when creating a new member; after that the member moves to “Past Member.” Select Uncancel Member Renewal to set the member back to ${props.noCharge ? "“Active Member.”" : "“Active Member,” and a renewal invoice will be sent automatically."}`}>
          {cancelButton}
        </ActionColumn>
        <ActionColumn helper="Use to immediately cancel a membership and remove all privileges, directory listings, and Member Dashboard access. The member’s status will change to “Deleted.” Member data can be recalled for 365 days when creating a new member; after that the member moves to “Past Member.”">
          {deleteButton}
        </ActionColumn>
      </div>
    </>
  );
};

export default MemberStatusModelPanel;
